/**
 * Module-level navigation history for the flat Drawer navigator.
 *
 * In Expo Router with a Drawer layout, navigating between sibling Drawer.Screen
 * items uses navigate() semantics (replace, not push), so router.canGoBack()
 * always returns false. This utility maintains its own history so BackButton
 * can navigate to the correct previous screen.
 *
 * Usage:
 *   - Call `navigationHistory.push(pathname)` on every route change (usePathname effect).
 *   - Call `navigationHistory.pop()` before navigating back; then read `.previous()`.
 */

const history: string[] = [];

export const navigationHistory = {
  /** Push a path if it differs from the current top of the stack. */
  push(path: string): void {
    if (history[history.length - 1] !== path) {
      history.push(path);
    }
  },

  /** Remove and return the current (top) path. */
  pop(): string | undefined {
    return history.pop();
  },

  /** Peek at the current top path without removing it. */
  current(): string | undefined {
    return history[history.length - 1];
  },

  /** Peek at the path one below the top (the "previous" screen). */
  previous(): string | undefined {
    return history[history.length - 2];
  },

  get length(): number {
    return history.length;
  },

  /** Reset the history — only for use in tests. */
  _reset(): void {
    history.length = 0;
  },
};
